import React, { useState } from 'react';
import { X, Sparkles, Loader, MapPin, DollarSign, Star } from 'lucide-react';
import { australianUniversities } from '../data/universities';

const API = process.env.REACT_APP_BACKEND_URL;

const ROOM_TYPES = ['Private Room', 'Shared Room', 'Studio', 'PBSA', 'Homestay', 'Any'];
const LIFESTYLES = ['Quiet & Study-focused', 'Social & Active', 'Flexible / No preference'];

export default function PropertyMatch({ onClose }) {
  const [step, setStep] = useState('form'); // form | loading | results
  const [form, setForm] = useState({
    university: '', budget_per_week: 350,
    room_type: 'Any', lifestyle: 'Flexible / No preference',
    move_in_date: '', preferences: ''
  });
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  const handleMatch = async () => {
    if (!form.university || !form.move_in_date) {
      setError('Please fill in your university and move-in date.');
      return;
    }
    setError('');
    setStep('loading');
    try {
      const res = await fetch(`${API}/api/ai/property-match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      const parsed = JSON.parse(data.raw);
      setResults(parsed);
      setStep('results');
    } catch {
      setError('Something went wrong. Please try again.');
      setStep('form');
    }
  };

  return (
    <div className="fixed inset-0 z-[8500] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#094f6f] to-sky-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-white" />
            <div>
              <p className="text-white font-bold">AI Property Match</p>
              <p className="text-white/70 text-xs">Powered by Claude Haiku</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 max-h-[80vh] overflow-y-auto">
          {step === 'form' && (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">Tell us about yourself and we'll find your top 3 matches.</p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your University *</label>
                <select value={form.university} onChange={e => setForm({...form, university: e.target.value})}
                  className="w-full border border-sky-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30">
                  <option value="">Select your university</option>
                  {australianUniversities.map((u, i) => (
                    <option key={i} value={u.name}>{u.name} — {u.location}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Weekly Budget: <span className="text-[#094f6f] font-bold">A${form.budget_per_week}</span>
                </label>
                <input type="range" min={150} max={800} step={10} value={form.budget_per_week}
                  onChange={e => setForm({...form, budget_per_week: parseInt(e.target.value)})}
                  className="w-full accent-[#094f6f]" />
                <div className="flex justify-between text-xs text-gray-400 mt-1"><span>A$150</span><span>A$800</span></div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room Type</label>
                  <select value={form.room_type} onChange={e => setForm({...form, room_type: e.target.value})}
                    className="w-full border border-sky-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30">
                    {ROOM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Move-in Date *</label>
                  <input type="date" value={form.move_in_date} onChange={e => setForm({...form, move_in_date: e.target.value})}
                    className="w-full border border-sky-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lifestyle</label>
                <div className="flex flex-wrap gap-2">
                  {LIFESTYLES.map(l => (
                    <button key={l} onClick={() => setForm({...form, lifestyle: l})}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        form.lifestyle === l ? 'bg-[#094f6f] text-white border-[#094f6f]' : 'border-gray-200 text-gray-600 hover:border-sky-300'
                      }`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Any extra preferences? <span className="text-gray-400">(optional)</span></label>
                <input value={form.preferences} onChange={e => setForm({...form, preferences: e.target.value})}
                  placeholder="e.g. pet-friendly, furnished, close to train station"
                  className="w-full border border-sky-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <button onClick={handleMatch}
                className="w-full bg-[#094f6f] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#094f6f]/90 transition-colors"
                data-testid="property-match-submit">
                <Sparkles className="w-4 h-4" />
                Find My Matches
              </button>
            </div>
          )}

          {step === 'loading' && (
            <div className="text-center py-16">
              <Loader className="w-10 h-10 text-[#094f6f] animate-spin mx-auto mb-4" />
              <p className="font-semibold text-gray-800">Finding your perfect matches...</p>
              <p className="text-sm text-gray-500 mt-1">Analysing {australianUniversities.length}+ properties across Australia</p>
            </div>
          )}

          {step === 'results' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="font-bold text-gray-900">Your Top 3 Matches</p>
                <button onClick={() => setStep('form')} className="text-xs text-[#094f6f] underline">Refine search</button>
              </div>
              {results.map((r, i) => (
                <div key={i} className="border border-sky-100 rounded-xl p-4 hover:shadow-md transition-shadow bg-sky-50/30">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{r.name}</p>
                      <p className="text-xs text-gray-500">{r.type} · {r.suburb}</p>
                    </div>
                    <div className="flex items-center gap-1 bg-[#094f6f] text-white text-xs px-2 py-1 rounded-full">
                      <Star className="w-3 h-3 fill-white" />
                      {r.match_score}% match
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
                    <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{r.price_range}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{r.suburb}</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-2">{r.why_match}</p>
                  <div className="bg-sky-100 text-[#094f6f] text-xs px-2.5 py-1 rounded-full inline-block font-medium">
                    ✨ {r.highlight}
                  </div>
                </div>
              ))}
              <button onClick={onClose}
                className="w-full bg-[#094f6f] text-white py-3 rounded-xl font-semibold hover:bg-[#094f6f]/90 transition-colors mt-2">
                Browse Properties
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
